import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';

export type QuickReadAloudAttempt = {
    requestId: string; promptId: string; promptParagraph: string; examName: string; status: string;
    isSuccessful: boolean | null; ipAddress: string | null; userAgent: string | null;
    audioSizeBytes: number; audioDuration: number | null; transcript: string | null;
    rawScore: number | null; maxScore: number | null; scorePercentage: number | null;
    processingDurationMs: number; errorMessage: string | null; createdAt: string; completedAt: string | null;
};

export type QuickReadAloudAttemptDetail = QuickReadAloudAttempt & {
    audioFileName: string | null; audioContentType: string | null; cloudflareRayId: string | null;
    referrer: string | null; origin: string | null; acceptLanguage: string | null; traceIdentifier: string | null;
    requestPayload: unknown; markingResponse: unknown; feedback: unknown;
};

type Envelope<T> = { success?: boolean; data?: T; errorMessage?: string | null; message?: string };

function config() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const base = String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1').replace(/\/+$/, '');
    const root = /\/api\/v[^/]+$/i.test(base) ? base : `${base}/api/v${String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1')}`;
    const token = String(session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '');
    return { root, token };
}

async function request<T>(path: string): Promise<T> {
    const { root, token } = config();
    const headers = new Headers({ Accept: 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(`${root}/AdminQuickReadAloud${path}`, { headers });
    const payload = await response.json().catch(() => null) as T | Envelope<T> | null;
    if (!response.ok) throw new Error((payload as Envelope<T> | null)?.errorMessage ?? `${response.status} ${response.statusText}`);
    if (payload && typeof payload === 'object' && 'success' in payload) {
        const envelope = payload as Envelope<T>;
        if (envelope.success === false) throw new Error(envelope.errorMessage ?? envelope.message ?? 'Request failed');
        return envelope.data as T;
    }
    return payload as T;
}

export function getQuickReadAloudAttempts(query: { page: number; perPage: number; isSuccessful?: boolean; ipAddress?: string }) {
    const params = new URLSearchParams({ page: String(query.page), perPage: String(query.perPage) });
    if (query.isSuccessful !== undefined) params.set('isSuccessful', String(query.isSuccessful));
    if (query.ipAddress?.trim()) params.set('ipAddress', query.ipAddress.trim());
    return request<{ total: number; items: QuickReadAloudAttempt[] }>(`?${params}`);
}

export function getQuickReadAloudAttempt(requestId: string) {
    return request<QuickReadAloudAttemptDetail>(`/${requestId}`);
}
